import React, { useContext, useEffect, useMemo, useReducer } from "react";
import { Collapse } from 'react-bootstrap';
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { MenuList } from './Menu';
import { usePermissions } from '../../context/PermissionsContext';
import { ThemeContext } from '../../context/ThemeContext';
import { FULL_VERSION, BUILD_SHA, BUILD_DATE } from '../../../version';

// Returns '#fff' or '#111' based on the best contrast for the given hex color.
function getContrastColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#111' : '#fff';
}

const reducer = (previousState, updatedState) => ({ ...previousState, ...updatedState });
const initialState = { active: "", activeSubmenu: "", activeLeafSubmenu: "", activeDeepSubmenu: "" };

// Recursively filter a menu item tree based on canRoute.
// Returns null if the item should be hidden entirely.
function filterItem(item, canRoute) {
  // Separators and titles always show
  if (item.classsChange === 'menu-separator' || item.classsChange === 'menu-title') return item;

  // Leaf item (has a `to`, no sub-content); placeholders with no `to` are hidden
  if (!item.content || item.content.length === 0) {
    return item.to && canRoute(item.to) ? item : null;
  }

  // Parent item - recursively filter children
  const filteredContent = item.content
    .map(child => filterItem(child, canRoute))
    .filter(Boolean);

  // Hide the parent if all children were filtered out
  if (filteredContent.length === 0) return null;
  return { ...item, content: filteredContent };
}

const SideBar = () => {
  const dat = new Date();
  const location = useLocation();
  const sideMenu = useSelector(state => state.sideMenu);
  const { layout, themeMode } = useContext(ThemeContext);
  const [state, setState] = useReducer(reducer, initialState);
  const handleMenuActive = status => {
    setState({ active: state.active === status ? "" : status, activeSubmenu: "" });
  };
  const handleSubmenuActive = status => {
    setState({ activeSubmenu: state.activeSubmenu === status ? "" : status, activeLeafSubmenu: "" });
  };
  const handleLeafSubmenuActive = status => {
    setState({ activeLeafSubmenu: state.activeLeafSubmenu === status ? "" : status, activeDeepSubmenu: "" });
  };
  const handleDeepSubmenuActive = status => {
    setState({ activeDeepSubmenu: state.activeDeepSubmenu === status ? "" : status });
  };
  // Close all expanded menus - used when navigating via a direct-link item
  const handleDirectNav = () => {
    setState({ active: "", activeSubmenu: "" });
  };

  // Current path for active detection
  const pathParts = location.pathname.split("/").filter(Boolean);
  const pathLast  = pathParts[pathParts.length - 1] || "";
  const pathFull  = pathParts.join("/");          // e.g. "worklists/wait-list"

  const { canRoute, loaded } = usePermissions();

  // Sync state.active to the section that contains the current path.
  // This handles navigation via flyout hover (bypasses handleMenuActive).
  useEffect(() => {
    function pathInItems(items) {
      for (const item of items) {
        const p = (item.to || '').replace(/^\//, '');
        if (p && (pathLast === p || pathFull === p)) return true;
        if (item.content?.length && pathInItems(item.content)) return true;
      }
      return false;
    }
    for (const item of MenuList) {
      if (!item.content?.length) continue;
      if (pathInItems(item.content)) {
        setState({ active: item.title });
        return;
      }
    }
    setState({ active: '' });
  }, [location.pathname, pathFull, pathLast]);

  // Only compute menu after permissions are confirmed - prevents a flash of the full menu.
  const filteredMenu = useMemo(
    () => loaded ? MenuList.map(item => filterItem(item, canRoute)).filter(Boolean) : [],
    [canRoute, loaded],
  );

  return (
    <>
    <style>{`
      /* ── Per-section color theming ─────────────────────────────────────── */

      /* Top-level: icon uses section color at rest */
      .deznav .metismenu > li > a i {
        color: var(--nav-color, var(--primary)) !important;
      }

      /* Top-level: hover text + icon use section color */
      .deznav .metismenu > li:hover > a,
      .deznav .metismenu > li:focus > a {
        color: var(--nav-color, var(--primary)) !important;
      }
      .deznav .metismenu > li:hover > a i,
      .deznav .metismenu > li:focus > a i {
        color: var(--nav-color, var(--primary)) !important;
      }

      /* Top-level: active state - section color background, contrast text + icon */
      [data-sidebar-style="full"][data-layout="vertical"] .deznav .metismenu > li.mm-active > a {
        background: var(--nav-color, var(--primary)) !important;
        color: var(--nav-contrast, #fff) !important;
      }
      [data-sidebar-style="full"][data-layout="vertical"] .deznav .metismenu > li.mm-active > a i {
        color: var(--nav-contrast, #fff) !important;
      }

      /* Level 1 – match weight of sub-level items */
      .deznav .metismenu > li > a .nav-text {
        font-weight: inherit !important;
      }

      /* All levels below 1 – align with level-1 icon */
      .deznav .metismenu > li > ul {
        padding-left: 0px !important;
      }
      .deznav .metismenu > li > ul ul {
        padding-left: 4px !important;
      }

      /* Level 3 – reduce font size */
      .deznav .metismenu > li > ul > li > ul > li > ul a {
        font-size: 0.78rem !important;
      }

      /* Level 4 – bullet before text */
      .deznav .metismenu > li > ul > li > ul > li > ul > li > ul a::before {
        content: '•';
        margin-right: 5px;
      }

      /* Level-2 item hover + selected - section color at 25% opacity, contrast text
         [data-sidebar-style] bumps specificity above the global .deznav .metismenu ul a:hover rule */
      [data-sidebar-style] .deznav .metismenu > li > ul > li > a:hover,
      [data-sidebar-style] .deznav .metismenu > li > ul > li.mm-active > a,
      [data-sidebar-style] .deznav .metismenu > li > ul > li > a.mm-active {
        background: rgba(var(--nav-color-rgb, 163, 54, 201), 0.5) !important;
        color: var(--nav-contrast, #fff) !important;
        border-radius: 0.375rem;
      }

      /* Level-3 item hover + selected - section color at 30% opacity, contrast text */
      [data-sidebar-style] .deznav .metismenu > li > ul > li > ul li > a:hover,
      [data-sidebar-style] .deznav .metismenu > li > ul > li > ul li > a.mm-active {
        background: rgba(var(--nav-color-rgb, 163, 54, 201), 0.3) !important;
        color: var(--nav-contrast, #fff) !important;
        border-radius: 0.375rem;
      }
    `}</style>
    <div className="deznav" style={{
      display: 'flex', flexDirection: 'column',
      ...(layout === 'horizontal' ? {
        backgroundColor: themeMode === 'dark' ? '#1f1e26' : '#F6F6F6',
        borderBottom: `1px solid ${themeMode === 'dark' ? '#343240' : '#e0e0e0'}`,
      } : {}),
    }}>
      <div className="deznav-scroll" style={{ flex: '1 1 0', minHeight: 0, height: 'auto' }}>
        {!loaded && (
          <div style={{ padding: '20px 24px', color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>
            <i className="las la-spinner la-spin me-2" />Loading...
          </div>
        )}
        <ul className="metismenu" id="menu">
          {filteredMenu.map((data, index) => {
            let menuClass = data.classsChange;
            if (menuClass === "menu-title") {
              return <li className={menuClass} key={index}>{data.title}</li>;
            }
            if (menuClass === "menu-separator") {
              return (
                <li key={index} style={{ listStyle: 'none', padding: '6px 20px 2px' }}>
                  <hr style={{ borderTop: '2px solid rgba(255,255,255,0.35)', margin: 0 }} />
                </li>
              );
            }

            const isDirect  = !data.content || data.content.length === 0;
            // Direct-link items are active when the current path matches data.to
            // and no accordion menu is open (prevents double-highlight while browsing)
            const isActive  = isDirect
              ? (state.active === '' && (pathLast === data.to || pathFull === data.to))
              : state.active === data.title;

            return (
              <li className={`has-menu ${isActive ? 'mm-active' : ''}`} key={index}
                style={{
                  ...(sideMenu ? { padding: '0 3px' } : {}),
                  ...(data.color ? { '--nav-color': data.color, '--nav-color-rgb': data.colorRgb, '--nav-contrast': getContrastColor(data.color) } : {}),
                }}>
                {!isDirect ? (
                  <Link to={"#"} className="has-arrow ai-icon" onClick={() => handleMenuActive(data.title)}>
                    {data.iconStyle}{" "}
                    <span className="nav-text">{data.title}</span>
                  </Link>
                ) : (
                  <Link to={data.to} onClick={handleDirectNav}>
                    {data.iconStyle}{" "}
                    <span className="nav-text">{data.title}</span>
                  </Link>
                )}
                <Collapse in={state.active === data.title}>
                  <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                    {data.content && data.content.map((sub, i) => {
                      const subPath = (sub.to || '').replace(/^\//, '');
                      const subDirectActive = !sub.content?.length && (pathLast === subPath || pathFull === subPath);
                      return (
                        <li key={i} className={`${state.activeSubmenu === sub.title ? "mm-active" : ""}`}>
                          {sub.content && sub.content.length > 0 ? (
                            <>
                              <Link to={sub.to} className={sub.hasMenu ? 'has-arrow' : ''}
                                onClick={() => handleSubmenuActive(sub.title)}>
                                {sub.title}
                              </Link>
                              <Collapse in={state.activeSubmenu === sub.title}>
                                <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                                  {sub.content.map((leaf, j) => {
                                    const leafPath = (leaf.to || '').replace(/^\//, '');
                                    const leafActive = pathLast === leafPath || pathFull === leafPath;
                                    if (leaf.content && leaf.content.length > 0) {
                                      return (
                                        <li key={j} className={state.activeLeafSubmenu === leaf.title ? 'mm-active' : ''}>
                                          <Link to="#" className="has-arrow"
                                            onClick={() => handleLeafSubmenuActive(leaf.title)}>
                                            {leaf.title}
                                          </Link>
                                          <Collapse in={state.activeLeafSubmenu === leaf.title}>
                                            <ul>
                                              {leaf.content.map((deep, k) => {
                                                const deepPath = (deep.to || '').replace(/^\//, '');
                                                const deepActive = pathLast === deepPath || pathFull === deepPath;
                                                if (deep.content && deep.content.length > 0) {
                                                  return (
                                                    <li key={k} className={state.activeDeepSubmenu === deep.title ? 'mm-active' : ''}>
                                                      <Link to="#" className="has-arrow"
                                                        onClick={() => handleDeepSubmenuActive(deep.title)}>
                                                        {deep.title}
                                                      </Link>
                                                      <Collapse in={state.activeDeepSubmenu === deep.title}>
                                                        <ul>
                                                          {deep.content.map((lvl5, m) => {
                                                            const lvl5Path = (lvl5.to || '').replace(/^\//, '');
                                                            const lvl5Active = pathLast === lvl5Path || pathFull === lvl5Path;
                                                            return (
                                                              <li key={m}>
                                                                <Link className={lvl5Active ? 'mm-active' : ''} to={lvl5.to}>
                                                                  {lvl5.title}
                                                                </Link>
                                                              </li>
                                                            );
                                                          })}
                                                        </ul>
                                                      </Collapse>
                                                    </li>
                                                  );
                                                }
                                                return (
                                                  <li key={k}>
                                                    <Link className={deepActive ? 'mm-active' : ''} to={deep.to}>
                                                      {deep.title}
                                                    </Link>
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          </Collapse>
                                        </li>
                                      );
                                    }
                                    return (
                                      <li key={j}>
                                        <Link className={leafActive ? "mm-active" : ""} to={leaf.to}>
                                          {leaf.title}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </Collapse>
                            </>
                          ) : (
                            <Link to={sub.to} className={subDirectActive ? 'mm-active' : ''}>{sub.title}</Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Collapse>
              </li>
            );
          })}
        </ul>
      </div>
      {!sideMenu && (
        <div className="copyright" style={{ flexShrink: 0 }}>
          <p><strong>Praxxis Partners LLC</strong> © {dat.getFullYear()} All Rights Reserved</p>
          <p
            className="version-line"
            title={[`Build ${BUILD_SHA}`, BUILD_DATE ? `Built ${BUILD_DATE}` : null].filter(Boolean).join('  •  ')}
            style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}
          >
            v{FULL_VERSION}
          </p>
        </div>
      )}
    </div>
    </>
  );
};

export default SideBar;
